import { useQuery } from '@tanstack/react-query';
import {
  type CreateItineraryComponentRequest,
  type ItineraryLeg,
  type TboFlightOption,
  type TboFlightSearchInput,
  type TboFlightSearchSegment,
  type TboFlightSegment,
  type TboHotelLiveOption,
  type TboHotelRoomOption,
} from '@utazzo/shared';
import {
  airportDisplayName,
  airportDistanceKm,
  airportDriveMinutes,
  driveTimeLabel,
} from '@utazzo/shared';
import {
  UzAlert,
  UzBadge,
  UzButton,
  UzCheckbox,
  UzDateTimePicker,
  UzInput,
  UzSelect,
} from '@utazzo/ui';
import { BedDouble, ChevronDown, Loader2, MapPin, Plane, Search, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { paxRoomsForStay } from '../stay-rooms';
import { fetchTboCityHotelCodes } from '../tbo.api';
import {
  useItineraryDetail,
  useTboFlightFareQuote,
  useTboFlightSearch,
  useTboHotelRooms,
  useTboHotelSearch,
} from '../use-itineraries';

import { ComponentCard } from './ComponentCard';
import { payloadToDraft } from './itinerary-draft';
import { MealBadge } from './MealBadge';
import { AdminOnlyAmount, PriceDelta, usePriceVisibility } from './price-visibility';

import { IataCityPicker } from '@/components/pickers';
import * as citiesApi from '@/features/cities/cities.api';
import { formatDMY } from '@/lib/date-format';

/**
 * Unified TBO picker (Phase 6). A big modal - not a drawer - that prefills the
 * search from the day/itinerary context (city -> TBO city / IATA, dates, pax),
 * runs the live search, and shows the full result set with sort + a refundable
 * filter. Reuses the proven search + payload logic from the old drawers.
 */

const CABIN_OPTIONS = [
  { value: 'ECONOMY', label: 'Economy' },
  { value: 'PREMIUM_ECONOMY', label: 'Premium economy' },
  { value: 'BUSINESS', label: 'Business' },
  { value: 'FIRST', label: 'First' },
];

const timeOf = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toTimeString().slice(0, 5);
};
/**
 * When a multi-city / open-jaw search comes back empty, TBO blanks the WHOLE
 * journey if any single leg has no seats (the journey is a cross-product of the
 * legs). The API reports which legs were empty; turn that into a clear
 * instruction naming the culprit leg instead of a dead-end "no fares".
 */
const emptyLegsMessage = (legs: TboFlightSearchSegment[] | undefined): string | null => {
  if (!legs || legs.length === 0) return null;
  const day = (d: string): string => formatDMY(d) || d;
  const list = legs
    .map((l) => `${l.origin} → ${l.destination} (${day(l.departureDate)})`)
    .join(', ');
  return legs.length === 1
    ? `No flights on ${list}. That leg has no seats for the selected date/passengers - change its date or airport and search again.`
    : `No flights on these legs: ${list}. One or more has no availability - adjust their dates/airports and search again.`;
};
const addDay = (iso: string, n: number): string => {
  // UTC-consistent parse + format (avoids an off-by-one in IST etc.).
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const fmtMoney = (paise: string, currency: string): string => {
  const n = Number(paise);
  if (!Number.isFinite(n)) return '-';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(n / 100);
  } catch {
    return `${currency} ${Math.round(n / 100).toLocaleString('en-IN')}`;
  }
};

// ---- Flight result filtering / sorting (client-side over the search response) ----
type FlightSort = 'cheapest' | 'fastest' | 'dep_early' | 'dep_late';
const FLIGHT_TIME_BANDS = [
  { key: 'early', label: 'Before 6 AM' },
  { key: 'morning', label: '6 AM - 12 PM' },
  { key: 'afternoon', label: '12 - 6 PM' },
  { key: 'evening', label: 'After 6 PM' },
] as const;
const hourOf = (iso?: string): number | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.getHours();
};
const bandOf = (iso?: string): string | null => {
  const h = hourOf(iso);
  if (h === null || h === undefined) return null;
  if (h < 6) return 'early';
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
};
/** Flight filters operate on the outbound leg (legs[0]). */
const optOut = (o: TboFlightOption): TboFlightSegment[] => o.legs[0] ?? [];
const optStops = (o: TboFlightOption): number => Math.max(0, optOut(o).length - 1);
const optDuration = (o: TboFlightOption): number =>
  optOut(o).reduce((s, sg) => s + (sg.durationMinutes || 0), 0);
/**
 * Airports the outbound leg's results use at one end, as the website's filter
 * rail shows them: the searched airport first (no distance line), then the
 * others cheapest-first with their distance / drive time from it. TBO answers
 * a metro search with every airport it files under the city (DEL brings Noida
 * and Hindon), so the agent needs to see which airport a fare is from.
 */
interface AirportFacet {
  code: string;
  name: string;
  minFarePaise: number;
  /** "29 km · ~1 h drive" for a secondary airport; null for the searched one. */
  detail: string | null;
}
const airportFacetsOf = (
  all: TboFlightOption[],
  end: 'departure' | 'arrival',
  searchedCode: string,
  legIndex = 0,
): AirportFacet[] => {
  const m = new Map<string, { name: string; min: number; count: number }>();
  for (const o of all) {
    const leg = o.legs[legIndex] ?? [];
    const seg = end === 'departure' ? leg[0] : leg[leg.length - 1];
    if (!seg) continue;
    const code = end === 'departure' ? seg.origin : seg.destination;
    if (!code) continue;
    const fare = Number(o.totalFarePaise);
    const cur = m.get(code);
    if (!cur) {
      m.set(code, {
        name:
          end === 'departure'
            ? airportDisplayName(code, seg.originAirport, seg.originCity)
            : airportDisplayName(code, seg.destinationAirport, seg.destinationCity),
        min: fare,
        count: 1,
      });
    } else {
      cur.count += 1;
      if (fare < cur.min) cur.min = fare;
    }
  }
  if (m.size === 0) return [];
  const searched = searchedCode.trim().toUpperCase();
  const main =
    searched && m.has(searched)
      ? searched
      : ([...m.entries()].sort((a, b) => b[1].count - a[1].count)[0]?.[0] ?? '');
  return [...m.entries()]
    .map(([code, v]) => {
      const km = code === main ? null : airportDistanceKm(main, code);
      const mins = code === main ? null : airportDriveMinutes(main, code);
      return {
        code,
        name: v.name,
        minFarePaise: v.min,
        detail:
          km === null ? null : mins === null ? `${km} km` : `${km} km · ${driveTimeLabel(mins)}`,
      };
    })
    .sort((a, b) => {
      if (a.code === main) return -1;
      if (b.code === main) return 1;
      return a.minFarePaise - b.minFarePaise;
    });
};
const optLayoverCities = (o: TboFlightOption): string[] =>
  optOut(o)
    .slice(0, -1)
    .map((s) => s.destinationCity || s.destination)
    .filter((c): c is string => Boolean(c));
/** "YYYY-MM-DD" -> local Date (midnight), for UzDateTimePicker. */
const ymdToDate = (s: string | null | undefined): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

/**
 * The full hotel stay for a city = its whole leg, not a single day. Walk the
 * legs in order summing nights to get each leg's check-in/check-out, then pick
 * the stay for this city (the one whose range contains the clicked day, else
 * the first). So clicking "Hotel" on any day of a 3-night Da Nang leg searches
 * the whole 22 -> 25 window, not just that one night.
 */
const computeStayRange = (
  detail: {
    travelStartDate?: string | null;
    legs: ItineraryLeg[];
    cruiseStays?: Array<{ embarkDate: string; nights: number }>;
  } | null,
  cityId: string | null,
  dayDate: string | null,
): { checkIn: string; checkOut: string } => {
  const start = detail?.travelStartDate ?? dayDate ?? '';
  const fallbackIn = dayDate ?? start;
  const fallback = { checkIn: fallbackIn, checkOut: fallbackIn ? addDay(fallbackIn, 1) : '' };
  if (!detail || !start || !cityId) return fallback;
  const legs = [...detail.legs].sort((a, b) => a.sortOrder - b.sortOrder);
  // Cruise stays occupy their own dated nights ([embark, embark + nights)), so
  // the contiguous leg cursor must HOP over them - otherwise a hotel leg that
  // follows (or precedes) a cruise is dated a night early and disagrees with
  // the day board. Mirrors the server's buildNightTimeline. The while loop
  // re-checks every cruise after each hop, so any hotel/cruise interleaving
  // resolves correctly.
  const cruiseRanges = (detail.cruiseStays ?? []).map((c) => ({
    start: c.embarkDate,
    end: addDay(c.embarkDate, c.nights),
  }));
  const hopOverCruises = (iso: string): string => {
    let cur = iso;
    let moved = true;
    while (moved) {
      moved = false;
      for (const r of cruiseRanges) {
        // ISO 'YYYY-MM-DD' strings compare correctly as plain strings.
        if (cur >= r.start && cur < r.end) {
          cur = r.end;
          moved = true;
        }
      }
    }
    return cur;
  };
  const ranges: Array<{ checkIn: string; checkOut: string }> = [];
  let cursor = start;
  for (const leg of legs) {
    const checkIn = hopOverCruises(cursor);
    const checkOut = addDay(checkIn, leg.nights);
    cursor = checkOut;
    if (leg.cityId === cityId) ranges.push({ checkIn, checkOut });
  }
  const first = ranges[0];
  if (!first) return fallback;
  if (dayDate) {
    const hit = ranges.find((r) => r.checkIn <= dayDate && dayDate < r.checkOut);
    if (hit) return hit;
  }
  return first;
};

/** Round-trip (bundle) edit context: both legs of the origin round, edited as
 *  one joint fare that drives both the outbound and the return component. */
interface BundleContext {
  outboundOrigin: string;
  outboundDestination: string;
  outboundDate: string;
  returnOrigin: string;
  returnDestination: string;
  returnDate: string;
  currentResultIndexes: string[];
  // Null when that direction has no component yet (first pick) - the caller's
  // submit then ADDS it instead of replacing.
  outboundReplaceId: string | null;
  returnReplaceId: string | null;
}

interface TboPickerModalProps {
  kind: 'hotel' | 'flight';
  itineraryId: string;
  dayDate: string | null;
  cityId: string | null;
  cityName: string | null;
  /** Origin city for a flight = where the traveller is coming from (the
   *  previous city, or the itinerary's departure city for the first leg). */
  originCityId?: string | null;
  /** Pre-resolved IATA codes for the From/To boxes. Preferred over resolving
   *  the city id -> IATA via getCity (which fails for v4 city ids and any city
   *  whose stored iata is null), so a one-way change always prefills the route
   *  it's editing. Sourced from the flight component being changed. */
  originIata?: string | null;
  destIata?: string | null;
  /** When editing an existing component: the option currently chosen. Drives
   *  the "Selected" badge + price delta (+/-) shown against each result. */
  currentResultIndex?: string | null;
  currentCostPaise?: string | null;
  /** Set when editing a bundled round-trip flight - switches to the joint
   *  two-leg picker that replaces both legs on pick. */
  bundle?: BundleContext | null;
  pending: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateItineraryComponentRequest) => void | Promise<void>;
  onSubmitBundle?: (
    outbound: CreateItineraryComponentRequest,
    ret: CreateItineraryComponentRequest,
  ) => void | Promise<void>;
}

export const TboPickerModal = (props: TboPickerModalProps): React.JSX.Element => {
  const { kind, itineraryId, dayDate, cityId, onClose } = props;
  const detail = useItineraryDetail(itineraryId).data ?? null;

  const dayCity = useQuery({
    queryKey: ['tbo-picker-city', cityId],
    queryFn: () => citiesApi.getCity(cityId as string),
    // Needed to resolve city -> IATA/tboCityId, and for flights also for the
    // city's neighbouring airports (the ready gate below does not wait on it
    // when the caller already passed the destination IATA).
    enabled: Boolean(cityId),
    retry: false, // a bad/v4 id fails deterministically - don't stall the gate
    staleTime: 60_000,
  });
  // Where the flight comes FROM: the explicit origin (previous city) when given,
  // else the itinerary's departure city (first leg).
  const originId = props.originCityId ?? detail?.fromCityId ?? null;
  const originCity = useQuery({
    queryKey: ['tbo-picker-city', originId],
    queryFn: () => citiesApi.getCity(originId as string),
    enabled: kind === 'flight' && Boolean(originId),
    retry: false,
    staleTime: 60_000,
  });

  const ready =
    detail !== null &&
    detail !== undefined &&
    (!cityId || dayCity.isFetched || (kind === 'flight' && Boolean(props.destIata))) &&
    (kind !== 'flight' || !originId || originCity.isFetched || Boolean(props.originIata));

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const adults = Math.max(1, detail?.adultsCount ?? 2);
  const children = detail?.childrenCount ?? 0;
  const infants = detail?.infantsCount ?? 0;
  const start = dayDate ?? detail?.travelStartDate ?? '';
  // Hotels default to the city's full stay; flights to the clicked day.
  const stay = computeStayRange(detail, cityId, dayDate);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-center justify-between border-b border-zinc-100 px-5 py-3.5">
          <div className="flex items-center gap-2">
            {kind === 'hotel' ? (
              <BedDouble className="h-4 w-4 text-violet-600" aria-hidden />
            ) : (
              <Plane className="h-4 w-4 text-sky-600" aria-hidden />
            )}
            <h3 className="text-sm font-semibold tracking-tight text-zinc-900">
              {kind === 'hotel'
                ? 'Search hotels'
                : props.bundle
                  ? 'Search round-trip flights'
                  : 'Search flights'}
            </h3>
            {props.cityName && (
              <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                <MapPin className="h-3 w-3" aria-hidden />
                {props.cityName}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {!ready ? (
          <div className="flex flex-1 items-center justify-center gap-2 text-sm text-zinc-400">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Preparing search...
          </div>
        ) : kind === 'flight' && props.bundle ? (
          <BundleFlightBody
            {...props}
            bundle={props.bundle}
            adults={adults}
            children={children}
            infants={infants}
          />
        ) : kind === 'hotel' ? (
          <HotelBody
            {...props}
            initialTboCityId={dayCity.data?.tboCityId ?? null}
            initialAdditionalTboCityIds={dayCity.data?.additionalTboCityIds ?? []}
            initialCheckIn={stay.checkIn}
            initialCheckOut={stay.checkOut}
            adults={adults}
            children={children}
          />
        ) : (
          <FlightBody
            {...props}
            initialOrigin={props.originIata || (originCity.data?.iataCode ?? '')}
            initialDestination={props.destIata || (dayCity.data?.iataCode ?? '')}
            initialDate={start}
            adults={adults}
            children={children}
            infants={infants}
          />
        )}
      </div>
    </div>
  );
};

// ---- Hotel ----

const HotelBody = ({
  itineraryId,
  cityId,
  cityName,
  pending,
  onSubmit,
  currentResultIndex,
  currentCostPaise,
  initialTboCityId,
  initialAdditionalTboCityIds,
  initialCheckIn,
  initialCheckOut,
}: TboPickerModalProps & {
  initialTboCityId: string | null;
  /** Extra TBO cities mapped to this City (nearby regions) - searched + merged. */
  initialAdditionalTboCityIds: string[];
  initialCheckIn: string;
  initialCheckOut: string;
  adults: number;
  children: number;
}): React.JSX.Element => {
  const detail = useItineraryDetail(itineraryId).data ?? null;
  const currency = detail?.currencyCode ?? 'INR';
  const [checkIn] = useState(initialCheckIn);
  const [checkOut] = useState(initialCheckOut);
  const [sort, setSort] = useState<'cheapest' | 'expensive' | 'rating'>('cheapest');
  // 2026-07-07. Refundable + meal-type filters are now multi-select checkboxes
  // in the left rail (matching the star-rating pattern) instead of a single
  // "Fare" dropdown up top. Empty set = no filter applied.
  const [refundSel, setRefundSel] = useState<Set<'REF' | 'NONREF'>>(new Set());
  const [mealSel, setMealSel] = useState<Set<'ROOM_ONLY' | 'BREAKFAST'>>(new Set());
  const [starsSel, setStarsSel] = useState<Set<number>>(new Set());
  // Distance bands (km) for the two location facts on each card. Null = any.
  const [maxStationKm, setMaxStationKm] = useState<number | null>(null);
  const [maxAirportKm, setMaxAirportKm] = useState<number | null>(null);
  const [nameQ, setNameQ] = useState('');
  const search = useTboHotelSearch();

  // Occupancy for this stay: the leg's per-stay room override when set, else
  // the itinerary's global room config, else a flat fallback (see
  // paxRoomsForStay). Real per-child ages drive TBO's age-band child pricing.
  const paxRooms = paxRoomsForStay(detail, cityId, checkIn);

  const canSearch = Boolean(initialTboCityId && checkIn && checkOut);
  const stayNights =
    checkIn && checkOut
      ? Math.max(
          1,
          Math.round((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 86_400_000),
        )
      : 1;

  // Progressive fan-out state. `progressive.options` accumulates as each
  // parallel Search batch resolves - the picker re-renders + re-sorts each
  // time so cheapest floats up as more hotels arrive. `batchesDone / total`
  // powers the "still searching" banner. `codesSearched` is the number of
  // hotel codes we sent to TBO (may be smaller than `totalMirrored` if the
  // mirror is huge and we capped it); useful in the banner so the operator
  // knows whether the picker is showing "everything TBO offered" or a subset.
  const [progressive, setProgressive] = useState<{
    options: TboHotelLiveOption[];
    batchesTotal: number;
    batchesDone: number;
    isLoading: boolean;
    /** True while the "Load remaining N hotels" fan-out is running. Distinct
     *  from `isLoading` so the initial results stay visible during the
     *  second pass. */
    isLoadingMore: boolean;
    /** Full mirror count (star-agnostic) - the raw "how many TBO hotels
     *  exist for this city" number. */
    totalMirrored: number;
    /** Count matching the star filter (3-star+); this is the target the
     *  progressive fan-out is walking through. */
    totalMatching: number;
    /** How many codes have been sent to TBO Search so far. When less than
     *  `totalMatching`, the "Load remaining" button is available. */
    codesSearched: number;
    error: string | null;
  }>({
    options: [],
    batchesTotal: 0,
    batchesDone: 0,
    isLoading: false,
    isLoadingMore: false,
    totalMirrored: 0,
    totalMatching: 0,
    codesSearched: 0,
    error: null,
  });
  // Aborts the current fan-out if the operator closes the modal or triggers
  // a new search while an old one is still running. Each batch checks the
  // ref before writing to state.
  const searchTokenRef = useRef(0);

  // Star-filter minimum. Fixed at 3 (matches legacy Utazzo policy - 1/2-star
  // inventory is never quoted). Applied server-side so we don't burn TBO
  // Search calls on hotels the operator would filter out anyway.
  const MIN_STAR_RATING = 3;
  // First-paint size: top 500 by starRating desc. Covers essentially every
  // 5-star and most 4-star hotels for even the biggest cities in one round.
  const FIRST_BATCH_LIMIT = 500;
  const BATCH_SIZE = 50;
  const CONCURRENCY = 6;

  /**
   * Fan out a list of hotel codes to TBO Search in bounded-concurrency
   * batches. Shared by the initial run + the "Load remaining" click so
   * dedup / progress tracking / abort semantics stay consistent.
   */
  const fanOutCodes = async (codes: string[], token: number): Promise<void> => {
    if (!initialTboCityId || !checkIn || !checkOut) return;
    const batches: string[][] = [];
    for (let i = 0; i < codes.length; i += BATCH_SIZE) {
      batches.push(codes.slice(i, i + BATCH_SIZE));
    }
    setProgressive((p) => ({
      ...p,
      batchesTotal: p.batchesTotal + batches.length,
      codesSearched: p.codesSearched + codes.length,
    }));
    if (batches.length === 0) return;

    const baseInput = {
      currencyCode: currency,
      tboCityId: initialTboCityId,
      additionalTboCityIds: initialAdditionalTboCityIds,
      checkIn,
      checkOut,
      paxRooms,
      guestNationality: 'IN',
      // Audit linkage: attribute the TBO call to this itinerary + the manual picker.
      itineraryId,
      source: 'MANUAL_PICKER' as const,
    };

    // Bounded concurrency worker pool. Firing all batches at once would
    // hammer TBO's rate limit; 6 concurrent Search calls stays inside
    // their fair-use envelope.
    let cursor = 0;
    const runOne = async (batch: string[]): Promise<void> => {
      try {
        const res = await search.mutateAsync({ ...baseInput, hotelCodes: batch });
        if (searchTokenRef.current !== token) return;
        setProgressive((p) => {
          // Dedupe on hotelCode - two batches shouldn't overlap but a
          // stale mirror could theoretically produce a duplicate.
          const seen = new Set(p.options.map((o) => o.hotelCode));
          const fresh = res.options.filter((o) => !seen.has(o.hotelCode));
          return {
            ...p,
            options: fresh.length > 0 ? [...p.options, ...fresh] : p.options,
            batchesDone: p.batchesDone + 1,
          };
        });
      } catch {
        if (searchTokenRef.current !== token) return;
        setProgressive((p) => ({ ...p, batchesDone: p.batchesDone + 1 }));
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, batches.length) }, async () => {
        while (cursor < batches.length) {
          const i = cursor++;
          const b = batches[i];
          if (!b) continue;
          await runOne(b);
        }
      }),
    );
  };

  const runSearch = async (): Promise<void> => {
    if (!initialTboCityId || !checkIn || !checkOut) return;
    const token = ++searchTokenRef.current;
    setProgressive({
      options: [],
      batchesTotal: 0,
      batchesDone: 0,
      isLoading: true,
      isLoadingMore: false,
      totalMirrored: 0,
      totalMatching: 0,
      codesSearched: 0,
      error: null,
    });
    try {
      // First pass: top FIRST_BATCH_LIMIT codes so first paint stays fast.
      // Server pre-filters by minStarRating so 1/2-star inventory never
      // hits TBO Search. `totalMatching` = eligible codes; `totalMirrored`
      // = raw mirror count (used only for status messaging when totalMirrored
      // > totalMatching, i.e. we filtered some out).
      const first = await fetchTboCityHotelCodes(initialTboCityId, FIRST_BATCH_LIMIT, {
        minStarRating: MIN_STAR_RATING,
        additionalTboCityIds: initialAdditionalTboCityIds,
      });
      if (searchTokenRef.current !== token) return;
      setProgressive((p) => ({
        ...p,
        totalMirrored: first.totalMirrored,
        totalMatching: first.totalMatching,
      }));
      await fanOutCodes(first.codes, token);
      if (searchTokenRef.current !== token) return;

      // Second and further passes: keep paging automatically until every
      // eligible code has been searched. No "Load remaining" button - the
      // operator asked for all matching hotels to stream in one after the
      // other without another click (Da Nang, Bali etc. have 1000+ eligible
      // hotels and hunting for the "more" button was slowing quotes down).
      // Cursor tracked locally, not from React state, so we're not gated on
      // a state-flush before the next iteration fires.
      let cursor = first.codes.length;
      let isMoreShown = false;
      while (cursor < first.totalMatching) {
        if (searchTokenRef.current !== token) return;
        if (!isMoreShown) {
          setProgressive((p) => ({ ...p, isLoadingMore: true }));
          isMoreShown = true;
        }
        const remaining = first.totalMatching - cursor;
        const next = await fetchTboCityHotelCodes(initialTboCityId, remaining, {
          minStarRating: MIN_STAR_RATING,
          offset: cursor,
          additionalTboCityIds: initialAdditionalTboCityIds,
        });
        if (searchTokenRef.current !== token) return;
        if (next.codes.length === 0) break;
        await fanOutCodes(next.codes, token);
        if (searchTokenRef.current !== token) return;
        cursor += next.codes.length;
      }
      setProgressive((p) => ({ ...p, isLoading: false, isLoadingMore: false }));
    } catch (e) {
      if (searchTokenRef.current !== token) return;
      setProgressive((p) => ({
        ...p,
        isLoading: false,
        isLoadingMore: false,
        error: e instanceof Error ? e.message : 'Search failed',
      }));
    }
  };

  // Auto-fire the initial search when the modal opens with everything
  // pre-filled. Guarded with a ref so React 18 StrictMode's double-invoke
  // (dev) and any accidental parent re-mount don't trigger two identical
  // TBO Search calls in parallel - which would race and only render one
  // response set, wasting the other.
  const autoSearchFiredRef = useRef(false);
  useEffect(() => {
    if (autoSearchFiredRef.current) return;
    if (!canSearch) return;
    autoSearchFiredRef.current = true;
    void runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const all = useMemo(
    // 3-star-and-above OR unrated. Server has already filtered the code list
    // by starRating >= 3 in `listHotelCodesForCity`, so anything that arrives
    // here without a starRating is a mirror-lag case (a code the server let
    // through where the live search + mirror couldn't confirm the rating).
    // Dropping those on the client caused a hard regression on prod when the
    // API was still on the pre-filter build: the client killed every option
    // that lacked a starRating and the picker went blank. Keep the loose
    // filter so the picker stays functional if the server's star gate is
    // ever bypassed, and let 1/2-star rows still be excluded here for the
    // rare cases where the server hasn't refreshed.
    () => progressive.options.filter((o) => !o.starRating || o.starRating >= 3),
    [progressive.options],
  );
  const starOptions = useMemo(() => {
    const s = new Set<number>();
    for (const o of all) if (o.starRating && o.starRating > 0) s.add(o.starRating);
    return [...s].sort((a, b) => b - a);
  }, [all]);
  const toggleStar = (n: number): void => {
    const x = new Set(starsSel);
    if (x.has(n)) x.delete(n);
    else x.add(n);
    setStarsSel(x);
  };
  const q = nameQ.trim().toLowerCase();
  const filtered = useMemo(() => {
    const r = all.filter((o) => {
      // Refundable: at least one of the selected buckets must match. Empty
      // set = no refundable filter applied.
      if (refundSel.size) {
        const ok =
          (refundSel.has('REF') && o.isRefundable) || (refundSel.has('NONREF') && !o.isRefundable);
        if (!ok) return false;
      }
      // Meal type is not part of the TBO live-search list response - it's
      // only available on the per-hotel rooms fetch. Keep the checkboxes as
      // state so they're visible to the operator (with a hint) and forward
      // the selection into the rooms popup later. We DON'T hide hotels here
      // just because meal data isn't in the list payload; a "silent zero"
      // list would be worse UX than showing the full result set.
      if (starsSel.size && !(o.starRating && starsSel.has(o.starRating))) return false;
      // A hotel with no distance is hidden once a band is chosen: it cannot be
      // shown to be within it.
      if (
        maxStationKm !== null &&
        !(o.centralStation && o.centralStation.distanceKm <= maxStationKm)
      )
        return false;
      if (
        maxAirportKm !== null &&
        !(o.nearestAirport && o.nearestAirport.distanceKm <= maxAirportKm)
      )
        return false;
      if (q && !o.name.toLowerCase().includes(q)) return false;
      return true;
    });
    return [...r].sort((a, b) => {
      if (sort === 'rating') return (b.starRating ?? 0) - (a.starRating ?? 0);
      const diff = Number(a.pricePaise) - Number(b.pricePaise);
      return sort === 'expensive' ? -diff : diff;
    });
  }, [all, refundSel, mealSel, starsSel, maxStationKm, maxAirportKm, q, sort]);
  // Groups appear only when some result carries the distance (no railway in
  // Dubai, so no station group there). Counts are over the whole result set.
  const hasStationData = useMemo(() => all.some((o) => o.centralStation), [all]);
  const hasAirportData = useMemo(() => all.some((o) => o.nearestAirport), [all]);
  const countWithin = (pick: 'station' | 'airport', km: number): number =>
    all.filter((o) => {
      const leg = pick === 'station' ? o.centralStation : o.nearestAirport;
      return leg != null && leg.distanceKm <= km;
    }).length;
  const toggleRefund = (k: 'REF' | 'NONREF'): void => {
    const x = new Set(refundSel);
    if (x.has(k)) x.delete(k);
    else x.add(k);
    setRefundSel(x);
  };
  const toggleMeal = (k: 'ROOM_ONLY' | 'BREAKFAST'): void => {
    const x = new Set(mealSel);
    if (x.has(k)) x.delete(k);
    else x.add(k);
    setMealSel(x);
  };

  const add = (opt: TboHotelLiveOption, room?: TboHotelRoomOption): void => {
    void onSubmit({
      kind: 'HOTEL',
      source: 'TBO',
      // Room details live in metaJson (roomName/roomsBreakdown); the title
      // stays just the hotel name so headings don't turn into room blobs.
      title: room ? opt.name : `${opt.name}${opt.starRating ? ` · ${opt.starRating}*` : ''}`,
      tboHotelCode: opt.hotelCode,
      costPaise: room ? room.pricePaise : opt.pricePaise,
      isRefundable: room ? room.isRefundable : opt.isRefundable,
      startDate: checkIn,
      endDate: checkOut,
      metaJson: {
        provider: 'TBO',
        resultIndex: opt.resultIndex,
        // Progressive fan-out: each batch returns its own traceId. The most
        // recent mutation's traceId is on `search.data`; good enough for the
        // audit trail since the operator only picks one hotel per search.
        traceId: search.data?.traceId ?? null,
        currencyCode: room ? room.currencyCode : opt.currencyCode,
        starRating: opt.starRating,
        // 2026-07-05. Snapshot enriched-mirror fields onto the
        // saved component so the DaysTab hotel row renders name +
        // star + address + cover + room + meal WITHOUT a second
        // per-hotel live-details roundtrip on every load. The row
        // components read these off metaJson via ComponentCard.
        hotelName: opt.name,
        address: opt.address,
        neighborhoodName: opt.neighborhoodName,
        nearestLandmarks: opt.nearestLandmarks,
        centralStation: opt.centralStation,
        nearestAirport: opt.nearestAirport,
        coverPhotoUrl: opt.coverPhotoUrl,
        checkIn,
        checkOut,
        nights: stayNights,
        ...(room
          ? {
              roomName: room.name,
              mealType: room.mealType,
              bookingCode: room.bookingCode,
              // Per-room breakdown (one entry per configured room, in
              // PaxRooms order) so the day card can show "Room 1 (2A) ·
              // Room Only" instead of one comma-joined blob.
              roomsBreakdown: room.subRooms.map((sub, i) => ({
                name: sub.name,
                mealType: sub.mealType,
                adults: paxRooms[i]?.adults ?? null,
                children: paxRooms[i]?.children ?? 0,
                // Bake the per-child ages too so a re-price (publish / pay-now)
                // can reconstruct the exact occupancy this stay was quoted at,
                // honouring a per-stay room override without re-deriving pax.
                childAges: paxRooms[i]?.childAges ?? [],
              })),
            }
          : {}),
      },
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Top search bar - city + dates prefilled (read-only) */}
      <div className="flex flex-wrap items-end gap-2 border-b border-zinc-100 px-4 py-3">
        <div className="min-w-[140px] flex-1">
          <p className="mb-1 text-[11px] font-medium text-zinc-500">City</p>
          <div className="flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-zinc-50 px-3 text-sm text-zinc-700">
            <MapPin className="h-3.5 w-3.5 text-zinc-400" aria-hidden />
            <span className="truncate">{cityName ?? 'City'}</span>
          </div>
        </div>
        <div className="w-32">
          <UzDateTimePicker
            mode="date"
            displayFormat="dd-MM-yyyy"
            label="Check-in"
            value={ymdToDate(checkIn)}
            onValueChange={() => {}}
            disabled
          />
        </div>
        <div className="w-32">
          <UzDateTimePicker
            mode="date"
            displayFormat="dd-MM-yyyy"
            label="Check-out"
            value={ymdToDate(checkOut)}
            onValueChange={() => {}}
            disabled
          />
        </div>
        <UzButton onClick={runSearch} loading={progressive.isLoading} disabled={!canSearch}>
          <Search className="mr-1.5 h-4 w-4" aria-hidden />
          Search
        </UzButton>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Filter rail */}
        <aside className="hidden w-56 shrink-0 space-y-4 overflow-auto border-r border-zinc-100 p-3.5 md:block">
          <div>
            <p className="mb-1.5 text-[10px] font-semibold tracking-wider text-zinc-400 uppercase">
              Hotel name
            </p>
            <UzInput
              placeholder="Search by name"
              value={nameQ}
              onChange={(e) => setNameQ(e.target.value)}
            />
          </div>
          {all.length === 0 ? (
            <p className="text-xs text-zinc-400">Filters appear after a search.</p>
          ) : (
            <>
              {/* 2026-07-07. Refundable + meal-type + star-rating filters live
                  side-by-side in the rail so the operator can toggle each
                  independently. Meal-type filtering requires per-hotel rooms
                  data (not in the search response), so those two checkboxes
                  carry a "narrows Rooms & Meals" hint - they don't hide
                  hotels from the list here. */}
              <FilterGroup title="Fare">
                <FilterCheck
                  label="Refundable"
                  checked={refundSel.has('REF')}
                  onToggle={() => toggleRefund('REF')}
                />
                <FilterCheck
                  label="Non-refundable"
                  checked={refundSel.has('NONREF')}
                  onToggle={() => toggleRefund('NONREF')}
                />
              </FilterGroup>
              <FilterGroup title="Meal type">
                <FilterCheck
                  label="Room only"
                  checked={mealSel.has('ROOM_ONLY')}
                  onToggle={() => toggleMeal('ROOM_ONLY')}
                />
                <FilterCheck
                  label="Breakfast"
                  checked={mealSel.has('BREAKFAST')}
                  onToggle={() => toggleMeal('BREAKFAST')}
                />
                <p className="mt-1 text-[10px] leading-snug text-zinc-400">
                  Applied inside Rooms &amp; Meals - hotels aren&apos;t hidden.
                </p>
              </FilterGroup>
              {hasStationData && (
                <FilterGroup title="Distance from central station">
                  <FilterCheck
                    label="Any"
                    checked={maxStationKm === null}
                    onToggle={() => setMaxStationKm(null)}
                  />
                  {CENTRAL_STATION_KM_BANDS.map((km) => (
                    <FilterCheck
                      key={km}
                      label={`Within ${km} km`}
                      checked={maxStationKm === km}
                      onToggle={() => setMaxStationKm(maxStationKm === km ? null : km)}
                      hint={String(countWithin('station', km))}
                    />
                  ))}
                </FilterGroup>
              )}
              {hasAirportData && (
                <FilterGroup title="Distance from airport">
                  <FilterCheck
                    label="Any"
                    checked={maxAirportKm === null}
                    onToggle={() => setMaxAirportKm(null)}
                  />
                  {AIRPORT_KM_BANDS.map((km) => (
                    <FilterCheck
                      key={km}
                      label={`Within ${km} km`}
                      checked={maxAirportKm === km}
                      onToggle={() => setMaxAirportKm(maxAirportKm === km ? null : km)}
                      hint={String(countWithin('airport', km))}
                    />
                  ))}
                </FilterGroup>
              )}
              {starOptions.length > 0 && (
                <FilterGroup title="Star rating">
                  {starOptions.map((s) => (
                    <FilterCheck
                      key={s}
                      label={`${s} star`}
                      checked={starsSel.has(s)}
                      onToggle={() => toggleStar(s)}
                    />
                  ))}
                </FilterGroup>
              )}
            </>
          )}
          {!initialTboCityId && (
            <p className="rounded bg-amber-50 px-2 py-1.5 text-[11px] text-amber-700">
              This city isn&apos;t mapped for live hotels, so hotels can&apos;t be searched.
            </p>
          )}
        </aside>

        {/* Results: count + sort + list */}
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex items-center justify-between gap-3 border-b border-zinc-100 px-4 py-2">
            <span className="text-xs text-zinc-500">
              {all.length > 0
                ? `${filtered.length} of ${all.length} hotels${progressive.isLoading || progressive.isLoadingMore ? ' searching so far' : ''}`
                : progressive.isLoading
                  ? 'Searching...'
                  : ''}
              {/* {progressive.totalMatching > 0 && (
                <span className="ml-2 text-zinc-400">
                  · searched {progressive.codesSearched} of {progressive.totalMatching}
                  {progressive.totalMirrored > progressive.totalMatching && (
                    <>
                      {' '}(3★+ · {progressive.totalMirrored - progressive.totalMatching}{' '}
                      lower-star hidden)
                    </>
                  )}
                </span>
              )} */}
            </span>
            <div className="w-44">
              <UzSelect
                value={sort}
                onValueChange={(v) => setSort(v as 'cheapest' | 'expensive' | 'rating')}
                options={[
                  { value: 'cheapest', label: 'Price: low to high' },
                  { value: 'expensive', label: 'Price: high to low' },
                  { value: 'rating', label: 'Star rating' },
                ]}
              />
            </div>
          </div>
          {/* Progressive-load banner. Two states:
                - loading: live batch counter + progress bar + "cheapest
                  updates as more arrive" nudge.
                - done: static "searched N codes, TBO returned X hotels"
                  breakdown so the operator understands the funnel (mirror
                  N -> live-availability M -> filters applied). */}
          {/* {progressive.batchesTotal > 0 && (
            <div
              className={
                progressive.isLoading
                  ? 'border-b border-sky-100 bg-sky-50/60 px-4 py-2'
                  : 'border-b border-zinc-100 bg-zinc-50/60 px-4 py-2'
              }
            >
              <div
                className={
                  'flex items-center justify-between text-[11px] ' +
                  (progressive.isLoading ? 'text-sky-800' : 'text-zinc-600')
                }
              >
                <span className="inline-flex items-center gap-1.5">
                  {progressive.isLoading && (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                      Batch {progressive.batchesDone} of {progressive.batchesTotal} · searching{' '}
                      {progressive.codesSearched} of {progressive.totalMirrored} mirrored hotels
                    </>
                  )}
                </span>
                {progressive.isLoading && (
                  <span className="tabular-nums">
                    {sort === 'cheapest'
                      ? '"Cheapest" updates as more arrive'
                      : 'Sort updates as more arrive'}
                  </span>
                )}
              </div>
              {progressive.isLoading && (
                <div className="mt-1 h-1 overflow-hidden rounded-full bg-sky-100">
                  <div
                    className="h-full bg-sky-500 transition-all duration-300"
                    style={{
                      width: `${Math.round((progressive.batchesDone / progressive.batchesTotal) * 100)}%`,
                    }}
                  />
                </div>
              )}
            </div>
          )} */}
          <div className="min-h-0 flex-1 overflow-auto p-4">
            {progressive.error ? (
              <UzAlert tone="destructive">
                Hotel search failed: {progressive.error}. Confirm live search is configured, then
                retry.
              </UzAlert>
            ) : progressive.isLoading && all.length === 0 ? (
              <Loading />
            ) : filtered.length === 0 ? (
              <Empty
                text={
                  progressive.batchesTotal > 0
                    ? all.length === 0
                      ? 'No hotels for those dates.'
                      : 'No hotels match your filters.'
                    : 'Run a search to see hotels.'
                }
              />
            ) : (
              <div className="space-y-2">
                {filtered.map((opt) => (
                  <HotelResultCard
                    key={opt.hotelCode}
                    opt={opt}
                    checkIn={checkIn}
                    checkOut={checkOut}
                    nights={stayNights}
                    paxRooms={paxRooms}
                    currency={currency}
                    itineraryId={itineraryId}
                    busy={pending}
                    selected={!!currentResultIndex && opt.resultIndex === currentResultIndex}
                    currentCostPaise={currentCostPaise}
                    mealSel={mealSel}
                    onAdd={add}
                  />
                ))}
                {/* Passive "streaming more" indicator - runSearch now auto-continues
                    through every remaining code without operator interaction, so
                    the old "Load remaining N hotels" button was removed. This
                    just tells the operator that more hotels are on the way when
                    the second-pass fan-out is active. */}
                {progressive.isLoadingMore && !progressive.error && (
                  <div className="mt-2 flex flex-col items-center gap-1.5 rounded-md border border-dashed border-zinc-300 bg-zinc-50/60 px-4 py-3">
                    <span className="text-xs text-zinc-500">
                      Streaming {progressive.totalMatching - progressive.codesSearched} more 3★+
                      hotels…
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/** A hotel search result that expands to show its live room options (per the
 *  family config), so the agent picks hotel + room in one go. */
const HotelResultCard = ({
  opt,
  checkIn,
  checkOut,
  nights,
  paxRooms,
  currency,
  itineraryId,
  busy,
  selected,
  currentCostPaise,
  mealSel,
  onAdd,
}: {
  opt: TboHotelLiveOption;
  checkIn: string;
  checkOut: string;
  nights: number;
  paxRooms: { adults: number; children: number; childAges: number[] }[];
  currency: string;
  itineraryId: string;
  busy: boolean;
  selected: boolean;
  currentCostPaise?: string | null;
  mealSel: Set<'ROOM_ONLY' | 'BREAKFAST'>;
  onAdd: (opt: TboHotelLiveOption, room?: TboHotelRoomOption) => void;
}): React.JSX.Element => {
  const [open, setOpen] = useState(false);
  const roomsQuery = useTboHotelRooms(
    open
      ? {
          hotelCode: opt.hotelCode,
          checkIn,
          checkOut,
          paxRooms,
          guestNationality: 'IN',
          currencyCode: currency,
        }
      : null,
  );
  const rooms = roomsQuery.data?.rooms ?? [];
  // Meal-type filter (from the left rail). Meal data only exists at the
  // room level, so it narrows the room options here rather than hiding
  // hotels. Empty selection = show every room.
  const shownRooms = useMemo(() => {
    if (mealSel.size === 0) return rooms;
    return rooms.filter((r) => {
      const mt = (r.mealType ?? '').toLowerCase();
      const isBreakfast = mt.includes('breakfast');
      const isRoomOnly = mt === '' || mt.includes('room only') || mt.includes('room_only');
      return (mealSel.has('BREAKFAST') && isBreakfast) || (mealSel.has('ROOM_ONLY') && isRoomOnly);
    });
  }, [rooms, mealSel]);

  // The list-search price (opt.pricePaise) is TBO's summarised cheapest fare,
  // which can undershoot the actual bookable rooms for this occupancy. So it's
  // shown as "from ... (indicative)" until the detailed rooms load; once they
  // do, the cheapest real bookable room becomes the authoritative header price.
  const cheapestRoomPaise = useMemo(() => {
    let min: number | null = null;
    for (const r of rooms) {
      const p = Number(r.pricePaise);
      if (Number.isFinite(p) && (min === null || p < min)) min = p;
    }
    return min;
  }, [rooms]);
  const roomsPriced = cheapestRoomPaise !== null;
  const headerPaise = roomsPriced ? String(cheapestRoomPaise) : opt.pricePaise;

  return (
    <div className={selected ? 'rounded-xl ring-2 ring-emerald-400/70' : undefined}>
      <ComponentCard
        editable={false}
        onEdit={() => {}}
        onRemove={() => {}}
        fetchLiveDetails={false}
        component={payloadToDraft(
          {
            kind: 'HOTEL',
            source: 'TBO',
            title: opt.name,
            tboHotelCode: opt.hotelCode,
            costPaise: headerPaise,
            isRefundable: opt.isRefundable,
            startDate: checkIn,
            endDate: checkOut,
            metaJson: {
              starRating: opt.starRating,
              currencyCode: opt.currencyCode,
              checkIn,
              checkOut,
              nights,
              // 2026-07-05. Enriched by the mirror-join on the API
              // side (see tbo-hotel.service.ts). Passed through to
              // ComponentCard so the picker rows render address +
              // cover without a second per-hotel details fetch.
              address: opt.address,
              neighborhoodName: opt.neighborhoodName,
              nearestLandmarks: opt.nearestLandmarks,
              centralStation: opt.centralStation,
              nearestAirport: opt.nearestAirport,
              coverPhotoUrl: opt.coverPhotoUrl,
            },
          },
          null,
          itineraryId,
        )}
        actionSlot={
          <div className="flex flex-col items-end gap-1">
            {!roomsPriced && (
              <span
                className="text-[10px] font-medium text-amber-600"
                title="TBO summary price; open rooms for the exact bookable rate"
              >
                from · indicative
              </span>
            )}
            {selected ? (
              <UzBadge tone="success">Selected</UzBadge>
            ) : (
              <PriceDelta base={currentCostPaise} now={headerPaise} currency={opt.currencyCode} />
            )}
            <UzButton
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => setOpen((o) => !o)}
            >
              {open ? 'Hide rooms' : 'Select room'}
              <ChevronDown
                className={'ml-1 h-3 w-3 transition-transform ' + (open ? 'rotate-180' : '')}
                aria-hidden
              />
            </UzButton>
          </div>
        }
      />
      {open && (
        <div className="mt-1 space-y-1.5 rounded-xl border border-zinc-200 bg-zinc-50/60 p-3">
          {roomsQuery.isLoading ? (
            <div className="flex items-center gap-2 py-2 text-xs text-zinc-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              Fetching rooms...
            </div>
          ) : roomsQuery.isError || rooms.length === 0 ? (
            <div className="flex items-center justify-between gap-2 text-xs text-zinc-500">
              <span>No room-level options returned; add at the listed price.</span>
              <UzButton
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                loading={busy}
                onClick={() => onAdd(opt)}
              >
                Add
              </UzButton>
            </div>
          ) : shownRooms.length === 0 ? (
            <div className="py-2 text-xs text-zinc-500">
              None of the {rooms.length} room{rooms.length === 1 ? '' : 's'} match the meal filter.
              Clear it to see all options.
            </div>
          ) : (
            shownRooms.map((r, i) => {
              // A "combo offer" is TBO's atomic booking unit: one BookingCode
              // covering every PaxRooms block in the request. When the operator
              // configured 2 rooms in Configure, `r.subRooms` has 2 entries -
              // one per requested room - and a single combined price.
              const isCombo = r.subRooms.length > 1;
              return (
                <div
                  key={r.bookingCode ?? i}
                  className="flex flex-col gap-2 rounded-lg border border-zinc-200 bg-white p-2.5"
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      {isCombo ? (
                        <p className="text-xs font-semibold text-zinc-900">
                          Combo offer / {r.subRooms.length} rooms
                        </p>
                      ) : (
                        <p className="text-xs font-medium break-words text-zinc-900">{r.name}</p>
                      )}
                      {!isCombo && (
                        <div className="mt-0.5 flex flex-wrap gap-1.5">
                          <MealBadge mealType={r.mealType} />
                          {r.isRefundable ? (
                            <UzBadge tone="success">Refundable</UzBadge>
                          ) : (
                            <UzBadge tone="secondary">Non-refundable</UzBadge>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-col items-end">
                      <AdminOnlyAmount
                        costPaise={r.pricePaise}
                        currency={r.currencyCode}
                        className="text-xs font-semibold text-zinc-900 tabular-nums"
                      />
                      {isCombo && (
                        <span className="text-[10px] text-zinc-500">total for all rooms</span>
                      )}
                      <PriceDelta
                        base={currentCostPaise}
                        now={r.pricePaise}
                        currency={r.currencyCode}
                      />
                    </div>
                    <UzButton
                      size="sm"
                      variant="outline"
                      className="h-7 shrink-0 text-xs"
                      loading={busy}
                      onClick={() => onAdd(opt, r)}
                    >
                      {isCombo ? 'Select combo' : 'Select'}
                    </UzButton>
                  </div>

                  {isCombo && (
                    <ul className="ml-1 space-y-1 border-l-2 border-zinc-200 pl-3">
                      {r.subRooms.map((sub, si) => {
                        const pax = paxRooms[si];
                        return (
                          <li key={si} className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="bg-primary/10 text-primary inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold">
                              {si + 1}
                            </span>
                            <span className="min-w-0 flex-1 font-medium break-words text-zinc-800">
                              {sub.name}
                            </span>
                            {pax && (
                              <span className="text-[11px] text-zinc-500">
                                ({pax.adults}A{pax.children > 0 ? ` / ${pax.children}C` : ''})
                              </span>
                            )}
                            <MealBadge mealType={sub.mealType} />
                            {r.isRefundable ? (
                              <UzBadge tone="success">Refundable</UzBadge>
                            ) : (
                              <UzBadge tone="secondary">Non-refundable</UzBadge>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ---- Flight ----

const FlightBody = ({
  itineraryId,
  pending,
  onSubmit,
  currentResultIndex,
  currentCostPaise,
  initialOrigin,
  initialDestination,
  initialDate,
  adults,
  children,
  infants,
}: TboPickerModalProps & {
  initialOrigin: string;
  initialDestination: string;
  initialDate: string;
  adults: number;
  children: number;
  infants: number;
}): React.JSX.Element => {
  const { canSeeAmounts } = usePriceVisibility();
  // The origin/destination IATA codes are resolved by async city lookups in the
  // parent, so they can arrive AFTER this body first mounts (origin especially -
  // its city isn't pre-cached like the day's destination is). Keep them in state
  // but sync when the resolved prop lands, so the fields prefill and the initial
  // search fires once both endpoints are known.
  const [origin, setOrigin] = useState(initialOrigin);
  const [destination, setDestination] = useState(initialDestination);
  const [date, setDate] = useState(initialDate);
  useEffect(() => {
    if (initialOrigin) setOrigin(initialOrigin);
  }, [initialOrigin]);
  useEffect(() => {
    if (initialDestination) setDestination(initialDestination);
  }, [initialDestination]);
  useEffect(() => {
    if (initialDate) setDate(initialDate);
  }, [initialDate]);
  const [cabinClass, setCabinClass] = useState<TboFlightSearchInput['cabinClass']>('ECONOMY');
  const [sort, setSort] = useState<FlightSort>('cheapest');
  const [refund, setRefund] = useState<'all' | 'ref' | 'nonref'>('all');
  const [airlinesSel, setAirlinesSel] = useState<Set<string>>(new Set());
  const [stopsSel, setStopsSel] = useState<Set<number>>(new Set());
  const [bandsSel, setBandsSel] = useState<Set<string>>(new Set());
  const [arrBandsSel, setArrBandsSel] = useState<Set<string>>(new Set());
  const [layoverSel, setLayoverSel] = useState<Set<string>>(new Set());
  // Departure / arrival airport filters (a metro search spans several airports).
  const [depAirportsSel, setDepAirportsSel] = useState<Set<string>>(new Set());
  const [arrAirportsSel, setArrAirportsSel] = useState<Set<string>>(new Set());
  // "Up to" caps for the price + duration sliders; null = no cap.
  const [priceCap, setPriceCap] = useState<number | null>(null);
  const [durCap, setDurCap] = useState<number | null>(null);
  const search = useTboFlightSearch();
  const fareQuote = useTboFlightFareQuote();

  const canSearch = Boolean(origin) && Boolean(destination) && Boolean(date);

  const runSearch = (): void => {
    if (!canSearch) return;
    search.mutate({
      currencyCode: 'INR',
      segments: [
        {
          origin: origin.toUpperCase(),
          destination: destination.toUpperCase(),
          departureDate: date,
        },
      ],
      adults,
      children,
      infants,
      cabinClass,
      directOnly: false,
      // Audit linkage: attribute the TBO call to this itinerary + the manual picker.
      itineraryId,
      source: 'MANUAL_PICKER',
    });
  };

  // Fire the initial search once both endpoints resolve. The guard is RESET on
  // effect cleanup so React StrictMode's dev-only mount -> unmount -> remount
  // (and any genuine remount) re-fires on the SURVIVING mount. Without the reset
  // the search fires on the discarded first mount, whose mutation observer is
  // torn down before the response lands - so the result is delivered to a dead
  // observer and the remounted body stays stuck on "Searching..." forever. FD
  // hits this because its ready-gate resolves both IATA codes before mounting,
  // so canSearch is already true at first mount (inside the discard window).
  const didAutoSearch = useRef(false);
  useEffect(() => {
    if (canSearch && !didAutoSearch.current) {
      didAutoSearch.current = true;
      runSearch();
    }
    return () => {
      didAutoSearch.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSearch]);

  const all = useMemo(() => search.data?.options ?? [], [search.data]);
  const depAirports = useMemo(() => airportFacetsOf(all, 'departure', origin), [all, origin]);
  const arrAirports = useMemo(
    () => airportFacetsOf(all, 'arrival', destination),
    [all, destination],
  );
  // A fresh search resets the price/duration caps (the old range no longer applies)
  // and DEFAULTS the airport filters to the primary (hub) airport - e.g. DEL / BKK -
  // so the operator sees the main-airport flights first; neighbouring-airport options
  // (Noida, Hindon, DMK...) stay one tick away in the filter. If the hub itself has no
  // results for an end, leave that end unfiltered so nothing is hidden by surprise.
  useEffect(() => {
    setPriceCap(null);
    setDurCap(null);
    const hubDep = origin.trim().toUpperCase();
    const hubArr = destination.trim().toUpperCase();
    setDepAirportsSel(depAirports.some((a) => a.code === hubDep) ? new Set([hubDep]) : new Set());
    setArrAirportsSel(arrAirports.some((a) => a.code === hubArr) ? new Set([hubArr]) : new Set());
  }, [all, depAirports, arrAirports, origin, destination]);

  const airlineOptions = useMemo(() => {
    const m = new Map<string, { name: string; min: number }>();
    for (const o of all) {
      const s = optOut(o)[0];
      if (!s) continue;
      const fare = Number(o.totalFarePaise);
      const cur = m.get(s.airline);
      if (!cur) m.set(s.airline, { name: s.airlineName || s.airline, min: fare });
      else if (fare < cur.min) cur.min = fare;
    }
    return [...m.entries()]
      .map(([code, v]) => [code, v.name, v.min] as const)
      .sort((a, b) => a[1].localeCompare(b[1]));
  }, [all]);
  // Price + duration ranges for the "up to" sliders.
  const priceRange = useMemo(() => {
    const p = all.map((o) => Number(o.totalFarePaise));
    return { min: p.length ? Math.min(...p) : 0, max: p.length ? Math.max(...p) : 0 };
  }, [all]);
  const durRange = useMemo(() => {
    const d = all.map(optDuration);
    return { min: d.length ? Math.min(...d) : 0, max: d.length ? Math.max(...d) : 0 };
  }, [all]);
  const curCode = all[0]?.currencyCode ?? 'INR';
  const layoverOptions = useMemo(() => {
    const set = new Set<string>();
    for (const o of all) optLayoverCities(o).forEach((c) => set.add(c));
    return [...set].sort();
  }, [all]);
  const stopOptions = useMemo(() => {
    const s = new Set<number>();
    for (const o of all) s.add(Math.min(2, optStops(o)));
    return [...s].sort((a, b) => a - b);
  }, [all]);

  const toggleSet = <T,>(
    set: Set<T>,
    setFn: React.Dispatch<React.SetStateAction<Set<T>>>,
    val: T,
  ): void => {
    const n = new Set(set);
    if (n.has(val)) n.delete(val);
    else n.add(val);
    setFn(n);
  };

  const filtered = useMemo(() => {
    const r = all.filter((o) => {
      if (refund === 'ref' && !o.isRefundable) return false;
      if (refund === 'nonref' && o.isRefundable) return false;
      if (airlinesSel.size && !airlinesSel.has(optOut(o)[0]?.airline ?? '')) return false;
      if (stopsSel.size && !stopsSel.has(Math.min(2, optStops(o)))) return false;
      if (bandsSel.size) {
        const b = bandOf(optOut(o)[0]?.departureAt);
        if (!b || !bandsSel.has(b)) return false;
      }
      if (arrBandsSel.size) {
        const leg = optOut(o);
        const a = bandOf(leg[leg.length - 1]?.arrivalAt);
        if (!a || !arrBandsSel.has(a)) return false;
      }
      if (priceCap !== null && Number(o.totalFarePaise) > priceCap) return false;
      if (durCap !== null && optDuration(o) > durCap) return false;
      if (layoverSel.size && !optLayoverCities(o).some((c) => layoverSel.has(c))) return false;
      if (depAirportsSel.size && !depAirportsSel.has(optOut(o)[0]?.origin ?? '')) return false;
      if (arrAirportsSel.size) {
        const leg = optOut(o);
        if (!arrAirportsSel.has(leg[leg.length - 1]?.destination ?? '')) return false;
      }
      return true;
    });
    return [...r].sort((a, b) => {
      if (sort === 'cheapest') return Number(a.totalFarePaise) - Number(b.totalFarePaise);
      if (sort === 'fastest') return optDuration(a) - optDuration(b);
      const ta = hourOf(optOut(a)[0]?.departureAt) ?? 0;
      const tb = hourOf(optOut(b)[0]?.departureAt) ?? 0;
      return sort === 'dep_early' ? ta - tb : tb - ta;
    });
  }, [
    all,
    refund,
    airlinesSel,
    stopsSel,
    bandsSel,
    arrBandsSel,
    priceCap,
    durCap,
    layoverSel,
    depAirportsSel,
    arrAirportsSel,
    sort,
  ]);

  const busy = pending || fareQuote.isPending;

  const add = (opt: TboFlightOption): void => {
    const traceId = search.data?.traceId ?? '';
    const first = optOut(opt)[0];
    const carrier = first ? `${first.airline}${first.flightNumber}` : '';
    const finalize = (lockedFarePaise: string, expiresAt: string | null): void => {
      void onSubmit({
        kind: 'FLIGHT',
        source: 'TBO',
        title: `${origin.toUpperCase()} - ${destination.toUpperCase()}${carrier ? ` · ${carrier}` : ''}`,
        costPaise: lockedFarePaise,
        isRefundable: opt.isRefundable,
        fromLocation: origin.toUpperCase(),
        toLocation: destination.toUpperCase(),
        startDate: date,
        startTime: first ? timeOf(first.departureAt) : null,
        metaJson: {
          provider: 'TBO',
          resultIndex: opt.resultIndexes[0],
          traceId,
          currencyCode: opt.currencyCode,
          fareBasis: opt.fareBasis,
          outbound: opt.legs[0] ?? [],
          returns: opt.legs[1] ?? [],
          expiresAt,
        },
      });
    };
    if (!traceId) {
      finalize(opt.totalFarePaise, null);
      return;
    }
    fareQuote.mutate(
      { resultIndex: opt.resultIndexes[0] ?? '', traceId, currencyCode: 'INR' },
      {
        onSuccess: (q) => finalize(q.totalFarePaise, q.expiresAt),
        onError: () => finalize(opt.totalFarePaise, null),
      },
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Top search bar - route/date prefilled (read-only), cabin editable */}
      <div className="flex flex-wrap items-end gap-2 border-b border-zinc-100 px-4 py-3">
        {/* Route is fixed to the city hub IATA (read-only). We search the hub and
            let TBO return nearby-airport departures automatically - the same as the
            public website - rather than making the operator pick an airport. The
            neighbouring airports come back in the results, where the departure /
            arrival airport filter chips below expose them. */}
        <div className="min-w-[140px] flex-1">
          <IataCityPicker label="From" value={origin || null} onValueChange={() => {}} disabled />
        </div>
        <div className="min-w-[140px] flex-1">
          <IataCityPicker
            label="To"
            value={destination || null}
            onValueChange={() => {}}
            disabled
          />
        </div>
        <div className="w-32 shrink-0">
          <UzDateTimePicker
            mode="date"
            displayFormat="dd-MM-yyyy"
            label="Departure"
            value={ymdToDate(date)}
            onValueChange={() => {}}
            disabled
          />
        </div>
        <div className="w-36 shrink-0">
          <UzSelect
            label="Cabin"
            options={CABIN_OPTIONS}
            value={cabinClass}
            onValueChange={(v) => setCabinClass(v as TboFlightSearchInput['cabinClass'])}
          />
        </div>
        <UzButton
          className="shrink-0"
          onClick={runSearch}
          loading={search.isPending}
          disabled={!canSearch}
        >
          <Search className="mr-1.5 h-4 w-4" aria-hidden />
          Search
        </UzButton>
        <div className="w-40 shrink-0">
          <UzSelect
            label="Fare"
            value={refund}
            onValueChange={(v) => setRefund(v as 'all' | 'ref' | 'nonref')}
            options={[
              { value: 'all', label: 'All fares' },
              { value: 'ref', label: 'Refundable only' },
              { value: 'nonref', label: 'Non-refundable' },
            ]}
          />
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Filter rail */}
        <aside className="hidden w-56 shrink-0 space-y-4 overflow-auto border-r border-zinc-100 p-3.5 md:block">
          {all.length === 0 ? (
            <p className="text-xs text-zinc-400">Filters appear after a search.</p>
          ) : (
            <>
              <FilterGroup title="Stops">
                {stopOptions.map((s) => (
                  <FilterCheck
                    key={s}
                    label={s === 0 ? 'Non-stop' : s === 1 ? '1 stop' : '2+ stops'}
                    checked={stopsSel.has(s)}
                    onToggle={() => toggleSet(stopsSel, setStopsSel, s)}
                  />
                ))}
              </FilterGroup>
              {/* Departure / arrival airports - only when the results span more
                  than one at that end, as on the website's filter rail. A lone hub
                  (e.g. BKK with no DMK in the results) has nothing to filter, so the
                  group is suppressed; a neighbour appears the moment TBO returns one. */}
              {depAirports.length > 1 && (
                <FilterGroup title="Departure airports">
                  {depAirports.map((a) => (
                    <FilterCheck
                      key={a.code}
                      label={a.name}
                      detail={a.detail}
                      checked={depAirportsSel.has(a.code)}
                      onToggle={() => toggleSet(depAirportsSel, setDepAirportsSel, a.code)}
                      hint={canSeeAmounts ? fmtMoney(String(a.minFarePaise), curCode) : undefined}
                    />
                  ))}
                </FilterGroup>
              )}
              {arrAirports.length > 1 && (
                <FilterGroup title="Arrival airports">
                  {arrAirports.map((a) => (
                    <FilterCheck
                      key={a.code}
                      label={a.name}
                      detail={a.detail}
                      checked={arrAirportsSel.has(a.code)}
                      onToggle={() => toggleSet(arrAirportsSel, setArrAirportsSel, a.code)}
                      hint={canSeeAmounts ? fmtMoney(String(a.minFarePaise), curCode) : undefined}
                    />
                  ))}
                </FilterGroup>
              )}
              {/* The price filter's own labels are fares. Admin-only, like the
                  fares on the result rows themselves. */}
              {canSeeAmounts && (
                <MaxSlider
                  title="Price"
                  min={priceRange.min}
                  max={priceRange.max}
                  value={priceCap ?? priceRange.max}
                  onChange={setPriceCap}
                  format={(v) => fmtMoney(String(v), curCode)}
                />
              )}
              <MaxSlider
                title="Duration"
                min={durRange.min}
                max={durRange.max}
                value={durCap ?? durRange.max}
                onChange={setDurCap}
                format={fmtMinutes}
              />
              <FilterGroup title={origin ? `Departure from ${origin}` : 'Departure time'}>
                {FLIGHT_TIME_BANDS.map((b) => (
                  <FilterCheck
                    key={b.key}
                    label={b.label}
                    checked={bandsSel.has(b.key)}
                    onToggle={() => toggleSet(bandsSel, setBandsSel, b.key)}
                  />
                ))}
              </FilterGroup>
              <FilterGroup title={destination ? `Arrival at ${destination}` : 'Arrival time'}>
                {FLIGHT_TIME_BANDS.map((b) => (
                  <FilterCheck
                    key={b.key}
                    label={b.label}
                    checked={arrBandsSel.has(b.key)}
                    onToggle={() => toggleSet(arrBandsSel, setArrBandsSel, b.key)}
                  />
                ))}
              </FilterGroup>
              <CollapsibleFilterGroup
                title="Airlines"
                items={airlineOptions.map(([code, name, min]) => ({
                  key: code,
                  label: name,
                  checked: airlinesSel.has(code),
                  onToggle: () => toggleSet(airlinesSel, setAirlinesSel, code),
                  // Cheapest fare per airline - an amount, so admin-only.
                  hint: canSeeAmounts ? fmtMoney(String(min), curCode) : undefined,
                }))}
              />
              {layoverOptions.length > 0 && (
                <CollapsibleFilterGroup
                  title="Layover at"
                  items={layoverOptions.map((c) => ({
                    key: c,
                    label: c,
                    checked: layoverSel.has(c),
                    onToggle: () => toggleSet(layoverSel, setLayoverSel, c),
                  }))}
                />
              )}
            </>
          )}
        </aside>

        {/* Results: count + sort + list */}
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex items-center justify-between gap-3 border-b border-zinc-100 px-4 py-2">
            <span className="text-xs text-zinc-500">
              {search.data ? `${filtered.length} of ${all.length} flights` : ''}
            </span>
            <div className="w-36">
              <UzSelect
                triggerClassName="h-8 text-xs"
                value={sort}
                onValueChange={(v) => setSort(v as FlightSort)}
                options={[
                  { value: 'cheapest', label: 'Cheapest' },
                  { value: 'fastest', label: 'Fastest' },
                  { value: 'dep_early', label: 'Departure: earliest' },
                  { value: 'dep_late', label: 'Departure: latest' },
                ]}
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto p-4">
            {search.isPending ? (
              <Loading />
            ) : search.isError ? (
              <UzAlert tone="destructive">
                Flight search failed. Confirm live search is configured, then retry.
              </UzAlert>
            ) : filtered.length === 0 ? (
              <Empty
                text={
                  search.data
                    ? all.length === 0
                      ? (emptyLegsMessage(search.data.emptyLegs) ?? 'No fares for that route/date.')
                      : 'No flights match your filters.'
                    : 'Run a search to see flights.'
                }
              />
            ) : (
              <div className="space-y-2">
                {filtered.map((opt, i) => {
                  const leg = optOut(opt);
                  const first = leg[0];
                  const last = leg[leg.length - 1];
                  const isSelected =
                    !!currentResultIndex && opt.resultIndexes[0] === currentResultIndex;
                  return (
                    <div
                      key={`${opt.resultIndexes[0]}-${i}`}
                      className={isSelected ? 'rounded-xl ring-2 ring-emerald-400/70' : undefined}
                    >
                      <ComponentCard
                        editable={false}
                        onEdit={() => {}}
                        onRemove={() => {}}
                        component={payloadToDraft(
                          {
                            kind: 'FLIGHT',
                            source: 'TBO',
                            title: `${first?.origin ?? ''} - ${last?.destination ?? ''}`,
                            costPaise: opt.totalFarePaise,
                            isRefundable: opt.isRefundable,
                            fromLocation: first?.origin ?? null,
                            toLocation: last?.destination ?? null,
                            startDate: date,
                            metaJson: {
                              outbound: opt.legs[0] ?? [],
                              returns: opt.legs[1] ?? [],
                              currencyCode: opt.currencyCode,
                              fareBasis: opt.fareBasis,
                            },
                          },
                          null,
                          itineraryId,
                        )}
                        actionSlot={
                          isSelected ? (
                            <UzBadge tone="success">Selected</UzBadge>
                          ) : (
                            <div className="flex flex-col items-end gap-1">
                              <PriceDelta
                                base={currentCostPaise}
                                now={opt.totalFarePaise}
                                currency={opt.currencyCode}
                              />
                              <UzButton
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                loading={busy}
                                onClick={() => add(opt)}
                              >
                                Add
                              </UzButton>
                            </div>
                          )
                        }
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Round-trip (bundle) flight body ----

const bDuration = (o: TboFlightOption): number =>
  o.legs.flat().reduce((s, sg) => s + (sg.durationMinutes || 0), 0);
/** Stops PER leg (outbound, return...) - a round trip with a non-stop outbound
 *  and a 1-stop return is [0, 1], so it surfaces under BOTH "Non-stop" and
 *  "1 stop". Filtering matches when ANY leg has the selected stop count. */
const bLegStops = (o: TboFlightOption): number[] =>
  o.legs.map((leg) => Math.max(0, leg.length - 1));
const bAirlines = (o: TboFlightOption): string[] =>
  o.legs.map((leg) => leg[0]?.airline).filter((a): a is string => Boolean(a));
const bLayoverCities = (o: TboFlightOption): string[] =>
  o.legs
    .flatMap((leg) => leg.slice(0, -1).map((s) => s.destinationCity || s.destination))
    .filter((c): c is string => Boolean(c));

const BundleFlightBody = ({
  itineraryId,
  pending,
  onSubmitBundle,
  currentCostPaise,
  bundle,
  adults,
  children,
  infants,
}: TboPickerModalProps & {
  bundle: BundleContext;
  adults: number;
  children: number;
  infants: number;
}): React.JSX.Element => {
  const { canSeeAmounts } = usePriceVisibility();
  const detail = useItineraryDetail(itineraryId).data ?? null;
  const currency = detail?.currencyCode ?? 'INR';
  // Route codes are fixed to each city's hub IATA (read-only). We search the hub
  // on both legs and let TBO return nearby-airport departures itself - matching
  // the public website - instead of letting the operator pick an airport.
  const outOrigin = bundle.outboundOrigin.toUpperCase();
  const outDest = bundle.outboundDestination.toUpperCase();
  const retOrigin = bundle.returnOrigin.toUpperCase();
  const retDest = bundle.returnDestination.toUpperCase();
  const [cabinClass, setCabinClass] = useState<TboFlightSearchInput['cabinClass']>('ECONOMY');
  const [sort, setSort] = useState<'cheapest' | 'fastest'>('cheapest');
  const [refund, setRefund] = useState<'all' | 'ref' | 'nonref'>('all');
  const [airlinesSel, setAirlinesSel] = useState<Set<string>>(new Set());
  const [stopsSel, setStopsSel] = useState<Set<number>>(new Set());
  // Per-leg departure/arrival time-of-day filters (Goibibo-style), one pair per
  // leg of the round trip.
  const [outDepSel, setOutDepSel] = useState<Set<string>>(new Set());
  const [outArrSel, setOutArrSel] = useState<Set<string>>(new Set());
  const [retDepSel, setRetDepSel] = useState<Set<string>>(new Set());
  const [retArrSel, setRetArrSel] = useState<Set<string>>(new Set());
  const [layoverSel, setLayoverSel] = useState<Set<string>>(new Set());
  // Per-leg departure / arrival airport filters (a metro search spans airports).
  const [outDepAirSel, setOutDepAirSel] = useState<Set<string>>(new Set());
  const [outArrAirSel, setOutArrAirSel] = useState<Set<string>>(new Set());
  const [retDepAirSel, setRetDepAirSel] = useState<Set<string>>(new Set());
  const [retArrAirSel, setRetArrAirSel] = useState<Set<string>>(new Set());
  const [priceCap, setPriceCap] = useState<number | null>(null);
  const [durCap, setDurCap] = useState<number | null>(null);
  const search = useTboFlightSearch();

  const runSearch = (): void => {
    search.mutate({
      currencyCode: currency,
      segments: [
        { origin: outOrigin, destination: outDest, departureDate: bundle.outboundDate },
        { origin: retOrigin, destination: retDest, departureDate: bundle.returnDate },
      ],
      adults,
      children,
      infants,
      cabinClass,
      directOnly: false,
      // Audit linkage: attribute the TBO call to this itinerary + the manual picker.
      itineraryId,
      source: 'MANUAL_PICKER',
    });
  };

  useEffect(() => {
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const all = useMemo(() => search.data?.options ?? [], [search.data]);
  const outDepAirports = useMemo(
    () => airportFacetsOf(all, 'departure', outOrigin, 0),
    [all, outOrigin],
  );
  const outArrAirports = useMemo(() => airportFacetsOf(all, 'arrival', outDest, 0), [all, outDest]);
  const retDepAirports = useMemo(
    () => airportFacetsOf(all, 'departure', retOrigin, 1),
    [all, retOrigin],
  );
  const retArrAirports = useMemo(() => airportFacetsOf(all, 'arrival', retDest, 1), [all, retDest]);
  // A fresh search resets the caps and DEFAULTS each leg's airport filters to the
  // primary (hub) airport - e.g. DEL / BKK - so the operator sees the main-airport
  // round trip first; neighbouring-airport options stay one tick away. If a hub has
  // no results for an end, leave that end unfiltered.
  useEffect(() => {
    setPriceCap(null);
    setDurCap(null);
    const sel = (facets: AirportFacet[], hub: string): Set<string> => {
      const code = hub.trim().toUpperCase();
      return facets.some((a) => a.code === code) ? new Set([code]) : new Set();
    };
    setOutDepAirSel(sel(outDepAirports, outOrigin));
    setOutArrAirSel(sel(outArrAirports, outDest));
    setRetDepAirSel(sel(retDepAirports, retOrigin));
    setRetArrAirSel(sel(retArrAirports, retDest));
  }, [
    all,
    outDepAirports,
    outArrAirports,
    retDepAirports,
    retArrAirports,
    outOrigin,
    outDest,
    retOrigin,
    retDest,
  ]);

  const airlineOptions = useMemo(() => {
    const m = new Map<string, { name: string; min: number }>();
    for (const o of all) {
      const fare = Number(o.totalFarePaise);
      for (const leg of o.legs) {
        const s = leg[0];
        if (!s) continue;
        const cur = m.get(s.airline);
        if (!cur) m.set(s.airline, { name: s.airlineName || s.airline, min: fare });
        else if (fare < cur.min) cur.min = fare;
      }
    }
    return [...m.entries()]
      .map(([code, v]) => [code, v.name, v.min] as const)
      .sort((a, b) => a[1].localeCompare(b[1]));
  }, [all]);
  const stopOptions = useMemo(() => {
    const s = new Set<number>();
    // Per-leg: "Non-stop" shows if EITHER leg has a non-stop option, etc.
    for (const o of all) for (const st of bLegStops(o)) s.add(Math.min(2, st));
    return [...s].sort((a, b) => a - b);
  }, [all]);
  const layoverOptions = useMemo(() => {
    const set = new Set<string>();
    for (const o of all) bLayoverCities(o).forEach((c) => set.add(c));
    return [...set].sort();
  }, [all]);
  const priceRange = useMemo(() => {
    const p = all.map((o) => Number(o.totalFarePaise));
    return { min: p.length ? Math.min(...p) : 0, max: p.length ? Math.max(...p) : 0 };
  }, [all]);
  const durRange = useMemo(() => {
    const d = all.map(bDuration);
    return { min: d.length ? Math.min(...d) : 0, max: d.length ? Math.max(...d) : 0 };
  }, [all]);
  const curCode = all[0]?.currencyCode ?? currency;

  const toggle = <T,>(
    set: Set<T>,
    setFn: React.Dispatch<React.SetStateAction<Set<T>>>,
    val: T,
  ): void => {
    const n = new Set(set);
    if (n.has(val)) n.delete(val);
    else n.add(val);
    setFn(n);
  };

  const cur = bundle.currentResultIndexes;
  const isSelected = (o: TboFlightOption): boolean => {
    if (cur.length === 0) return false;
    const out = o.resultIndexes[0];
    const ret = o.resultIndexes[1] ?? o.resultIndexes[0];
    return out === cur[0] && ret === (cur[1] ?? cur[0]);
  };

  const legDep = (o: TboFlightOption, i: number): string | undefined => o.legs[i]?.[0]?.departureAt;
  const legArr = (o: TboFlightOption, i: number): string | undefined => {
    const leg = o.legs[i] ?? [];
    return leg[leg.length - 1]?.arrivalAt;
  };
  const bandMatch = (sel: Set<string>, iso: string | undefined): boolean => {
    if (!sel.size) return true;
    const b = bandOf(iso);
    return Boolean(b && sel.has(b));
  };

  const filtered = useMemo(() => {
    const r = all.filter((o) => {
      if (refund === 'ref' && !o.isRefundable) return false;
      if (refund === 'nonref' && o.isRefundable) return false;
      if (airlinesSel.size && !bAirlines(o).some((a) => airlinesSel.has(a))) return false;
      // Match if ANY leg has a selected stop count (per-leg, not the total).
      if (stopsSel.size && !bLegStops(o).some((st) => stopsSel.has(Math.min(2, st)))) return false;
      if (!bandMatch(outDepSel, legDep(o, 0))) return false;
      if (!bandMatch(outArrSel, legArr(o, 0))) return false;
      if (!bandMatch(retDepSel, legDep(o, 1))) return false;
      if (!bandMatch(retArrSel, legArr(o, 1))) return false;
      const legCode = (i: number, end: 'departure' | 'arrival'): string => {
        const leg = o.legs[i] ?? [];
        return (end === 'departure' ? leg[0]?.origin : leg[leg.length - 1]?.destination) ?? '';
      };
      if (outDepAirSel.size && !outDepAirSel.has(legCode(0, 'departure'))) return false;
      if (outArrAirSel.size && !outArrAirSel.has(legCode(0, 'arrival'))) return false;
      if (retDepAirSel.size && !retDepAirSel.has(legCode(1, 'departure'))) return false;
      if (retArrAirSel.size && !retArrAirSel.has(legCode(1, 'arrival'))) return false;
      if (priceCap !== null && Number(o.totalFarePaise) > priceCap) return false;
      if (durCap !== null && bDuration(o) > durCap) return false;
      if (layoverSel.size && !bLayoverCities(o).some((c) => layoverSel.has(c))) return false;
      return true;
    });
    return [...r].sort((a, b) =>
      sort === 'cheapest'
        ? Number(a.totalFarePaise) - Number(b.totalFarePaise)
        : bDuration(a) - bDuration(b),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    all,
    refund,
    airlinesSel,
    stopsSel,
    outDepSel,
    outArrSel,
    retDepSel,
    retArrSel,
    outDepAirSel,
    outArrAirSel,
    retDepAirSel,
    retArrAirSel,
    priceCap,
    durCap,
    layoverSel,
    sort,
  ]);

  const pick = (opt: TboFlightOption): void => {
    const traceId = search.data?.traceId ?? null;
    const outLeg = opt.legs[0] ?? [];
    const retLeg = opt.legs[1] ?? [];
    const outFirst = outLeg[0];
    const retFirst = retLeg[0];
    const baseMeta = {
      provider: 'TBO' as const,
      traceId,
      currencyCode: opt.currencyCode,
      fareBasis: opt.fareBasis,
      bundle: true,
    };
    void onSubmitBundle?.(
      {
        kind: 'FLIGHT',
        source: 'TBO',
        title: `${outOrigin} - ${outDest}`,
        costPaise: opt.totalFarePaise,
        isRefundable: opt.isRefundable,
        fromLocation: outOrigin,
        toLocation: outDest,
        startDate: bundle.outboundDate,
        startTime: outFirst ? timeOf(outFirst.departureAt) : null,
        metaJson: {
          ...baseMeta,
          resultIndex: opt.resultIndexes[0],
          outbound: outLeg,
          returns: [],
          bundleRole: 'outbound',
        },
      },
      {
        kind: 'FLIGHT',
        source: 'TBO',
        title: `${retOrigin} - ${retDest}`,
        // The fare lives on the outbound; the return is the same ticket.
        costPaise: '0',
        isRefundable: opt.isRefundable,
        fromLocation: retOrigin,
        toLocation: retDest,
        startDate: bundle.returnDate,
        startTime: retFirst ? timeOf(retFirst.departureAt) : null,
        metaJson: {
          ...baseMeta,
          resultIndex: opt.resultIndexes[1] ?? opt.resultIndexes[0],
          outbound: retLeg,
          returns: [],
          bundleRole: 'return',
          includedInBundle: true,
        },
      },
    );
  };

  /** A route end: the city hub IATA, shown read-only (no airport picker). */
  const routeEnd = (value: string): React.JSX.Element => (
    <span className="font-semibold">{value}</span>
  );
  const legChip = (
    label: string,
    from: React.JSX.Element,
    to: React.JSX.Element,
    date: string,
  ): React.JSX.Element => (
    <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs text-zinc-700">
      <span className="font-medium text-zinc-400">{label}</span>
      {from}
      <Plane className="h-3 w-3 rotate-90 text-zinc-400" aria-hidden />
      {to}
      <span className="text-zinc-400">· {formatDMY(date) || date}</span>
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Top search bar - route fixed to the city hub IATA (read-only), dates
          fixed, cabin editable. Nearby airports come back in the results. */}
      <div className="flex flex-wrap items-end gap-2 border-b border-zinc-100 px-4 py-3">
        <div className="flex flex-wrap items-end gap-2">
          {legChip('Outbound', routeEnd(outOrigin), routeEnd(outDest), bundle.outboundDate)}
          {legChip('Return', routeEnd(retOrigin), routeEnd(retDest), bundle.returnDate)}
        </div>
        <div className="w-36">
          <UzSelect
            label="Cabin"
            options={CABIN_OPTIONS}
            value={cabinClass}
            onValueChange={(v) => setCabinClass(v as TboFlightSearchInput['cabinClass'])}
          />
        </div>
        <UzButton onClick={runSearch} loading={search.isPending}>
          <Search className="mr-1.5 h-4 w-4" aria-hidden />
          Search
        </UzButton>
        <div className="ml-auto w-44">
          <UzSelect
            label="Fare"
            value={refund}
            onValueChange={(v) => setRefund(v as 'all' | 'ref' | 'nonref')}
            options={[
              { value: 'all', label: 'All fares' },
              { value: 'ref', label: 'Refundable only' },
              { value: 'nonref', label: 'Non-refundable' },
            ]}
          />
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Filter rail */}
        <aside className="hidden w-56 shrink-0 space-y-4 overflow-auto border-r border-zinc-100 p-3.5 md:block">
          {all.length === 0 ? (
            <p className="text-xs text-zinc-400">Filters appear after a search.</p>
          ) : (
            <>
              <FilterGroup title="Stops (either leg)">
                {stopOptions.map((s) => (
                  <FilterCheck
                    key={s}
                    label={s === 0 ? 'Non-stop' : s === 1 ? '1 stop' : '2+ stops'}
                    checked={stopsSel.has(s)}
                    onToggle={() => toggle(stopsSel, setStopsSel, s)}
                  />
                ))}
              </FilterGroup>
              <MaxSlider
                title="Round-trip price"
                min={priceRange.min}
                max={priceRange.max}
                value={priceCap ?? priceRange.max}
                onChange={setPriceCap}
                format={(v) => fmtMoney(String(v), curCode)}
              />
              <MaxSlider
                title="Total duration"
                min={durRange.min}
                max={durRange.max}
                value={durCap ?? durRange.max}
                onChange={setDurCap}
                format={fmtMinutes}
              />
              {(
                [
                  ['Outbound departure airports', outDepAirports, outDepAirSel, setOutDepAirSel],
                  ['Outbound arrival airports', outArrAirports, outArrAirSel, setOutArrAirSel],
                  ['Return departure airports', retDepAirports, retDepAirSel, setRetDepAirSel],
                  ['Return arrival airports', retArrAirports, retArrAirSel, setRetArrAirSel],
                ] as const
              ).map(([title, facets, sel, setSel]) =>
                // Only when a leg has more than one airport at that end - a lone hub
                // (e.g. BKK with no DMK) has nothing to filter; a neighbour appears
                // the moment TBO returns one.
                facets.length > 1 ? (
                  <FilterGroup key={title} title={title}>
                    {facets.map((a) => (
                      <FilterCheck
                        key={a.code}
                        label={a.name}
                        detail={a.detail}
                        checked={sel.has(a.code)}
                        onToggle={() => toggle(sel, setSel, a.code)}
                        hint={canSeeAmounts ? fmtMoney(String(a.minFarePaise), curCode) : undefined}
                      />
                    ))}
                  </FilterGroup>
                ) : null,
              )}
              <BundleTimeGroup
                title={`Departure from ${outOrigin}`}
                sel={outDepSel}
                onToggle={(k) => toggle(outDepSel, setOutDepSel, k)}
              />
              <BundleTimeGroup
                title={`Arrival at ${outDest}`}
                sel={outArrSel}
                onToggle={(k) => toggle(outArrSel, setOutArrSel, k)}
              />
              <BundleTimeGroup
                title={`Departure from ${retOrigin}`}
                sel={retDepSel}
                onToggle={(k) => toggle(retDepSel, setRetDepSel, k)}
              />
              <BundleTimeGroup
                title={`Arrival at ${retDest}`}
                sel={retArrSel}
                onToggle={(k) => toggle(retArrSel, setRetArrSel, k)}
              />
              <CollapsibleFilterGroup
                title="Airlines"
                items={airlineOptions.map(([code, name, min]) => ({
                  key: code,
                  label: name,
                  checked: airlinesSel.has(code),
                  onToggle: () => toggle(airlinesSel, setAirlinesSel, code),
                  // Cheapest fare per airline - an amount, so admin-only.
                  hint: canSeeAmounts ? fmtMoney(String(min), curCode) : undefined,
                }))}
              />
              {layoverOptions.length > 0 && (
                <CollapsibleFilterGroup
                  title="Layover at"
                  items={layoverOptions.map((c) => ({
                    key: c,
                    label: c,
                    checked: layoverSel.has(c),
                    onToggle: () => toggle(layoverSel, setLayoverSel, c),
                  }))}
                />
              )}
            </>
          )}
        </aside>

        {/* Results: count + sort + joint two-leg cards */}
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex items-center justify-between gap-3 border-b border-zinc-100 px-4 py-2">
            <span className="text-xs text-zinc-500">
              {search.data ? `${filtered.length} of ${all.length} round trips` : ''}
            </span>
            <div className="w-36">
              <UzSelect
                triggerClassName="h-8 text-xs"
                value={sort}
                onValueChange={(v) => setSort(v as 'cheapest' | 'fastest')}
                options={[
                  { value: 'cheapest', label: 'Cheapest' },
                  { value: 'fastest', label: 'Fastest' },
                ]}
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto p-4">
            {search.isPending ? (
              <Loading />
            ) : search.isError ? (
              <UzAlert tone="destructive">
                Round-trip search failed. Confirm live search is configured, then retry.
              </UzAlert>
            ) : filtered.length === 0 ? (
              <Empty
                text={
                  search.data
                    ? all.length === 0
                      ? (emptyLegsMessage(search.data.emptyLegs) ??
                        'No round-trip fares for those routes/dates.')
                      : 'No round trips match your filters.'
                    : 'Run a search to see round trips.'
                }
              />
            ) : (
              <div className="space-y-5">
                {filtered.map((opt, i) => {
                  const selected = isSelected(opt);
                  const outLeg = opt.legs[0] ?? [];
                  const retLeg = opt.legs[1] ?? [];
                  return (
                    <div
                      key={`${opt.resultIndexes.join('-')}-${i}`}
                      // The two legs inside are ComponentCards carrying the same
                      // `rounded-xl border-zinc-200` as this wrapper did, so every
                      // edge on screen read identically and a long scroll blurred
                      // into one list - no way to tell where a round trip ended.
                      // The bundle now outranks its own children: heavier + darker
                      // border, and a tinted tray the white leg cards sit ON, so
                      // "these two belong together" is legible at a glance.
                      className={
                        'space-y-2 rounded-xl border-2 p-3 shadow-sm transition-colors ' +
                        (selected
                          ? 'border-emerald-400 bg-emerald-50/50 ring-2 ring-emerald-400/40'
                          : 'border-zinc-300 bg-zinc-50 hover:border-zinc-400')
                      }
                    >
                      <ComponentCard
                        editable={false}
                        onEdit={() => {}}
                        onRemove={() => {}}
                        component={payloadToDraft(
                          {
                            kind: 'FLIGHT',
                            source: 'TBO',
                            title: `${bundle.outboundOrigin} - ${bundle.outboundDestination}`,
                            costPaise: opt.totalFarePaise,
                            isRefundable: opt.isRefundable,
                            fromLocation: outLeg[0]?.origin ?? bundle.outboundOrigin,
                            toLocation:
                              outLeg[outLeg.length - 1]?.destination ?? bundle.outboundDestination,
                            startDate: bundle.outboundDate,
                            metaJson: {
                              outbound: outLeg,
                              returns: [],
                              currencyCode: opt.currencyCode,
                              fareBasis: opt.fareBasis,
                              bundle: true,
                            },
                          },
                          null,
                          itineraryId,
                        )}
                      />
                      <ComponentCard
                        editable={false}
                        onEdit={() => {}}
                        onRemove={() => {}}
                        component={payloadToDraft(
                          {
                            kind: 'FLIGHT',
                            source: 'TBO',
                            title: `${bundle.returnOrigin} - ${bundle.returnDestination}`,
                            costPaise: '0',
                            isRefundable: opt.isRefundable,
                            fromLocation: retLeg[0]?.origin ?? bundle.returnOrigin,
                            toLocation:
                              retLeg[retLeg.length - 1]?.destination ?? bundle.returnDestination,
                            startDate: bundle.returnDate,
                            metaJson: {
                              outbound: retLeg,
                              returns: [],
                              currencyCode: opt.currencyCode,
                              includedInBundle: true,
                            },
                          },
                          null,
                          itineraryId,
                        )}
                      />
                      <div className="flex items-center justify-between gap-3 border-t border-zinc-300 px-1 pt-3">
                        <div className="flex items-baseline gap-2">
                          {canSeeAmounts && (
                            <>
                              <span className="text-[11px] text-zinc-500">Round trip total</span>
                              <AdminOnlyAmount
                                costPaise={opt.totalFarePaise}
                                currency={opt.currencyCode}
                                className="text-sm font-bold text-zinc-900 tabular-nums"
                              />
                            </>
                          )}
                          <PriceDelta
                            base={currentCostPaise}
                            now={opt.totalFarePaise}
                            currency={opt.currencyCode}
                          />
                        </div>
                        {selected ? (
                          <UzBadge tone="success">Selected</UzBadge>
                        ) : (
                          <UzButton
                            size="sm"
                            // Solid, not outline. This is the one action on the
                            // card and it used to be a 28px ghost-weight chip
                            // that vanished against the leg cards above it.
                            className="font-semibold shadow-sm"
                            loading={pending}
                            onClick={() => pick(opt)}
                          >
                            Select round trip
                          </UzButton>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Distance bands for the hotel location filters. Same values as the website's
 * results rail so an operator and a customer see the same choices.
 */
const CENTRAL_STATION_KM_BANDS = [2, 5, 10] as const;
const AIRPORT_KM_BANDS = [5, 10, 20, 30] as const;

const FilterGroup = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <div>
    <p className="mb-1.5 text-[10px] font-semibold tracking-wider text-zinc-400 uppercase">
      {title}
    </p>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const FilterCheck = ({
  label,
  checked,
  onToggle,
  hint,
  detail,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
  /** Optional right-aligned hint, e.g. the cheapest fare for an airline. */
  hint?: string;
  /** Optional quieter second line under the label, e.g. "29 km · ~1 h drive".
   *  A row with one wraps its label instead of truncating (airport names). */
  detail?: string | null;
}): React.JSX.Element => (
  <label
    className={`flex cursor-pointer gap-2 text-xs text-zinc-600 ${detail ? 'items-start' : 'items-center'}`}
  >
    <UzCheckbox
      checked={checked}
      onCheckedChange={onToggle}
      containerClassName={detail ? 'mt-0.5' : undefined}
    />
    <span className="min-w-0 flex-1">
      <span className={detail ? 'block' : 'block truncate'}>{label}</span>
      {detail && <span className="block text-[11px] text-zinc-400">{detail}</span>}
    </span>
    {hint && <span className="shrink-0 text-[11px] text-zinc-400 tabular-nums">{hint}</span>}
  </label>
);

/** "Xh Ym" from a minutes count. */
const fmtMinutes = (m: number): string => {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
};

/**
 * Single-handle "up to" slider - filters options whose value is at most the
 * chosen cap. Used for price and total-duration filters. Renders nothing when
 * every option shares the same value (no meaningful range).
 */
const MaxSlider = ({
  title,
  min,
  max,
  value,
  onChange,
  format,
}: {
  title: string;
  min: number;
  max: number;
  value: number;
  onChange: (v: number) => void;
  format: (v: number) => string;
}): React.JSX.Element | null => {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null;
  const step = Math.max(1, Math.round((max - min) / 50));
  return (
    <FilterGroup title={title}>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="accent-primary h-1 w-full cursor-pointer"
      />
      <div className="mt-1 flex items-center justify-between text-[10px] text-zinc-400 tabular-nums">
        <span>{format(min)}</span>
        <span className="font-medium text-zinc-600">Up to {format(value)}</span>
        <span>{format(max)}</span>
      </div>
    </FilterGroup>
  );
};

/**
 * A filter group that shows only the first few options and reveals the rest
 * behind a "Show more (+N)" toggle - keeps long airline/layover lists from
 * pushing the whole rail off-screen.
 */
const FILTER_PREVIEW_LIMIT = 5;
const CollapsibleFilterGroup = ({
  title,
  items,
}: {
  title: string;
  items: { key: string; label: string; checked: boolean; onToggle: () => void; hint?: string }[];
}): React.JSX.Element => {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, FILTER_PREVIEW_LIMIT);
  const hidden = items.length - shown.length;
  return (
    <FilterGroup title={title}>
      {shown.map((it) => (
        <FilterCheck
          key={it.key}
          label={it.label}
          checked={it.checked}
          onToggle={it.onToggle}
          hint={it.hint}
        />
      ))}
      {items.length > FILTER_PREVIEW_LIMIT && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="text-primary mt-0.5 text-[11px] font-medium hover:underline"
        >
          {expanded ? 'Show less' : `Show more (+${hidden})`}
        </button>
      )}
    </FilterGroup>
  );
};

/** A time-of-day band filter for one leg of a round-trip (dep or arr). */
const BundleTimeGroup = ({
  title,
  sel,
  onToggle,
}: {
  title: string;
  sel: Set<string>;
  onToggle: (key: string) => void;
}): React.JSX.Element => (
  <FilterGroup title={title}>
    {FLIGHT_TIME_BANDS.map((b) => (
      <FilterCheck
        key={b.key}
        label={b.label}
        checked={sel.has(b.key)}
        onToggle={() => onToggle(b.key)}
      />
    ))}
  </FilterGroup>
);

const Loading = (): React.JSX.Element => (
  <div className="flex items-center justify-center gap-2 py-10 text-sm text-zinc-400">
    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
    Searching...
  </div>
);
const Empty = ({ text }: { text: string }): React.JSX.Element => (
  <p className="py-10 text-center text-sm text-zinc-400">{text}</p>
);
